import { formatDate } from "@angular/common";
import { Injectable, signal } from "@angular/core";
import { AustralianState } from "@models/address/australian-state.enum";
import { Tag } from "@models/common/tag";
import { Company } from "@models/companies/company";
import { CompanyRecord } from "@models/companies/company-record";
import { CellDataType } from "@models/grid/cell-data-type.enum";
import { FilterOption } from "@models/grid/grid-filter";
import { JurisdictionType } from "@models/trusts/trusts-enums";
import {
  AgCompanyDebtComponent
} from "@shared/components/grid/ag-cells/common/ag-company-debt/ag-company-debt.component";
import {
  AgCompanyNameAcnComponent
} from "@shared/components/grid/ag-cells/common/ag-company-name-acn/ag-company-name-acn.component";
import { AgDateCellComponent } from "@shared/components/grid/ag-cells/common/ag-date-cell/ag-date-cell.component";
import { AgEntityTagsComponent } from "@shared/components/grid/ag-cells/common/ag-entity-tags/ag-entity-tags.component";
import {
  AgCompanyStatusComponent
} from "@shared/components/grid/ag-cells/companies/ag-company-status/ag-company-status.component";
import { ACNPipe } from "@shared/pipes/acnPipe";
import { BaseGridService } from "@shared/services/base/base-grid.service";
import { createFilterOptions } from "@shared/utils/create-filter-options.helper";
import { convertEnumToFilterOption } from "@shared/utils/enum-to-filter-option.helper";
import { applyTagGridFilterOption } from "@shared/utils/grid/apply-tag-grid-filter-option";
import { statusFilterOptions } from "@shared/utils/status-filter-options.helper";
import { uniqueArrayToFilterOptionHelper } from "@shared/utils/unique-array-to-filter-option.helper";
import { mergeUniqueValues } from "@shared/utils/unique-merge.helper";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { Subject } from "rxjs";

@Injectable()
export class CompaniesGridService extends BaseGridService {
  refreshFiltersTrigger$ = new Subject<void>();

  // filters
  readonly activeOptions = ['active', 'non-agent', 'deregistered', 'strike-off'];

  companyTagFilterOptions = signal<FilterOption[]>([]);
  companyAccountManagerFilterOptions = signal<FilterOption[]>([]);
  companyPartnerManagerFilterOptions = signal<FilterOption[]>([]);
  companyDebtOptions = signal<FilterOption[]>([]);
  companyDateOfEstablishmentFilterOptions = signal<FilterOption[]>([]);
  familyGroupFilterOptions = signal<FilterOption[]>([]);
  nextAnnualReviewDateFilterOptions = signal<FilterOption[]>([]);
  companyStatusFilterOptions = signal<FilterOption[]>([]);
  companyJurisdictionOptions = signal<FilterOption[]>([]);
  numberColumnForExport = signal(0);

  refreshFilters(): void {
    this.refreshFiltersTrigger$.next();
  }

  setGridFilterOptions(companies: CompanyRecord[]): void {
    if (!companies?.length) return;

    this.companyTagFilterOptions.set(createFilterOptions<Tag>(mergeUniqueValues<Tag[]>(companies
      .map(c => c.tags), 'tagId'), 'name', 'name'));

    const accountManagerList = companies?.filter(c => c.accountManager?.fullName)
      .map(c => c.accountManager?.fullName) as string[];
    this.companyAccountManagerFilterOptions.set(uniqueArrayToFilterOptionHelper([...new Set(accountManagerList)]));

    const partnerManagerList = companies?.filter(c => c.partnerManager?.fullName)
      .map(c => c.partnerManager?.fullName) as string[];
    this.companyPartnerManagerFilterOptions.set(uniqueArrayToFilterOptionHelper([...new Set(partnerManagerList)]));

    const familyGroupList = companies?.filter(c => c.familyGroup)
      .map(c => c.familyGroup!);
    this.familyGroupFilterOptions.set(uniqueArrayToFilterOptionHelper([...new Set(familyGroupList)]));

    const amountOwingList = companies.filter(company => company.companyDebt?.amountOwing !== undefined)
      ?.map((company) => company.companyDebt?.amountOwing.toString()) as string[];
    this.companyDebtOptions.set(uniqueArrayToFilterOptionHelper([...new Set(amountOwingList)]));

    const dateOfEstablishmentList = companies.filter(company => company.dateOfEstablishment)
      ?.map(company => company.dateOfEstablishment);
    this.companyDateOfEstablishmentFilterOptions.set([...new Set(dateOfEstablishmentList)].map(date => {
      return {
        label: formatDate(date ?? '', 'dd MMM yyyy', 'en'),
        value: date ?? '',
        active: false
      };
    }));

    const nextAnnualReviewDateList = companies.filter(company => company.nextAnnualReviewDate)
      ?.map(company => company.nextAnnualReviewDate);
    this.nextAnnualReviewDateFilterOptions.set([...new Set(nextAnnualReviewDateList)].map(reviewDate => {
      return {
        label: formatDate(reviewDate ?? '', 'dd MMM yyyy', 'en'),
        value: reviewDate ?? '',
        active: false
      };
    }));

    this.companyStatusFilterOptions.set(statusFilterOptions(this.activeOptions));

    const jurisdictionOptions = convertEnumToFilterOption(AustralianState, true);
    jurisdictionOptions.forEach(o => o.value = (JurisdictionType[o.value as string] as number).toString());
    jurisdictionOptions.sort((a, b) => a.label.localeCompare(b.label));
    this.companyJurisdictionOptions.set(jurisdictionOptions);
  }

  override setColDefs(actionColumn: ColDef) {
    const colDefs: ColDef[] = [
      {
        headerName: 'Company Name & ACN',
        suppressMovable: true,
        field: 'name',
        width: 200,
        flex: 2,
        editable: false,
        sort: 'asc',
        cellRenderer: AgCompanyNameAcnComponent,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: '',
        field: 'acn',
        width: 100,
        flex: 0.5,
        menuTabs: [],
        valueFormatter: (params: ValueFormatterParams<Company, string>) => {
          return ACNPipe.formatValue(params.value);
        },
        hide: true,
      },
      {
        headerName: 'Registration',
        field: 'dateOfEstablishment',
        width: 80,
        flex: 0.5,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.companyDateOfEstablishmentFilterOptions(),
        },
        getQuickFilterText: (params: { data: { acn: string } }) => {
          return params.data.acn ?? '';
        },
        cellDataType: CellDataType.DATE_STRING,
        cellRenderer: AgDateCellComponent,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 80,
        flex: 0.5,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.companyStatusFilterOptions(),
        },
        cellRenderer: AgCompanyStatusComponent,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Debt',
        field: 'companyDebt.amountOwing',
        width: 110,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.companyDebtOptions(),
        },
        cellRenderer: AgCompanyDebtComponent,
        cellDataType: CellDataType.NUMBER,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Tags',
        field: 'tags',
        width: 380,
        flex: 2,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.companyTagFilterOptions(),
        },
        cellRendererParams: {
          allowEdit: true,
          handleAddNewTag: (() => this.refreshFilters()),
          onClickTag: this.onClickTag.bind(this)
        },
        cellRenderer: AgEntityTagsComponent,
        valueFormatter: (params: { value: Tag }) => params.value?.name ?? '',
        comparator: (tagListA: Tag[], tagListB: Tag[]) => {
          const a = tagListA?.length ?? 0;
          const b = tagListB?.length ?? 0;
          return a - b;
        },
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Account Manager',
        field: 'accountManager.fullName',
        flex: 2,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.companyAccountManagerFilterOptions(),
        },
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Partner',
        field: 'partnerManager.fullName',
        flex: 2,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.companyPartnerManagerFilterOptions(),
        },
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Corporate Key',
        field: 'corporateKey',
        flex: 1,
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Reference Number',
        field: 'referenceNumber',
        flex: 1,
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Family Group',
        field: 'familyGroup',
        flex: 1,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.familyGroupFilterOptions(),
        },
        cellDataType: CellDataType.TEXT,
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Next Annual Review',
        field: 'nextAnnualReviewDate',
        flex: 1,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.nextAnnualReviewDateFilterOptions(),
        },
        cellDataType: CellDataType.DATE_STRING,
        cellRenderer: AgDateCellComponent,
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        headerName: 'Jurisdiction',
        field: 'jurisdiction',
        flex: 1,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.companyJurisdictionOptions(),
        },
        valueFormatter: (params: { value: string }) => {
          return this.companyJurisdictionOptions().find(o => o.value == params.value)?.label ?? '';
        },
        hide: true,
        menuTabs: ['generalMenuTab'],
      },
      {
        ...actionColumn,
      }
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));

    this.colDefs.set(colDefs);
  }

  onClickTag(tag: Tag): void {
    applyTagGridFilterOption(tag, this.colDefs, this.gridApi);
  }
}
